/**
 * Google Maps runtime-health reporting.
 *
 * The app loads Google Maps through a single seam (MapApiProvider), but until
 * now a runtime failure there was invisible: an invalid/over-quota/billing-
 * disabled key renders the greyed "For development purposes only" watermark and
 * throws nothing, so no log, metric, or alert ever fired. This module turns the
 * three detectable failure modes into a typed signal on the existing browser
 * telemetry pipeline (clientLogger → Loki) plus a Prometheus-bound counter
 * (web_maps_degraded).
 *
 * Detection is inherently limited by what Google exposes:
 *  - `auth_failure`      — `window.gm_authFailure` (invalid key / referrer / some
 *                          billing failures). A real callback.
 *  - `script_load_error` — `<APIProvider onError>` (the SDK script failed to load).
 *  - `dev_watermark`     — DOM sniffing. Google gives NO callback for the
 *                          quota/billing "for development purposes only" overlay,
 *                          so a MutationObserver watching for that literal text is
 *                          the only signal available. The brittleness is confined
 *                          to this file on purpose.
 */

import { clientLogger } from '@/shared/lib/client-logger';
import {
  reportWebMapsDegraded,
  type WebMapsDegradedReason,
} from '@/shared/lib/metrics/client-metrics_gen';
import { assertNever } from '@letsgethome/lib/utils/enums';

declare global {
  interface Window {
    /** Google Maps invokes this global on an auth/billing failure, if present. */
    gm_authFailure?: () => void;
  }
}

/**
 * The detectable Maps failure modes. NOT a hand-written union — an alias of the
 * generated `web_maps_degraded.reason` vocabulary, so this module can never emit
 * (or `messageFor`-handle) a reason the metric does not declare: a reason added
 * to `web/manifests/metrics.yaml` widens this type and the exhaustive `switch`
 * below then fails to compile until it is handled. See metrics.yaml.
 */
export type MapsFailureReason = WebMapsDegradedReason;

/**
 * The origin the Maps JS SDK is fetched from.
 *
 * Named because a failure to REACH it is a different fact from a failure once
 * reached, and only the first one is caused by a network that refused the
 * request — see {@link unreachableOriginFor}.
 */
const MAPS_SDK_ORIGIN = 'maps.googleapis.com';

/**
 * The third-party origin a reason means we could NOT REACH, or `null` when the
 * reason is not a reachability failure at all.
 *
 * ## Why this exists, and why it is a `switch`
 *
 * `script_load_error` is the only one of the three that means *the network did
 * not give us the file*. `auth_failure` fires after the script LOADED and
 * Google refused the key; `dev_watermark` fires after the map RENDERED. Saying
 * "unreachable" for either of those would be false.
 *
 * That distinction is load-bearing beyond this file. The e2e harness seals
 * third-party DNS in the HERMETIC posture (`THIRD_PARTY_DENIALS` in
 * `web/e2e/helpers/index.ts`, posture-shaped since #6192), and
 * `maps.googleapis.com` is inside the `*.googleapis.com` family it seals
 * whole there — so in that posture this module correctly reports
 * `script_load_error` every time a page embeds a map. (In the DEPLOYED
 * posture the Maps pair is carved out and the SDK really loads, which is what
 * makes `auth_failure` / `dev_watermark` reachable under test at all.) That report is the seal
 * WORKING, not a product defect, but it reaches the console from FIRST-PARTY
 * code, so the harness's "is this console error one my own seal caused?" rule
 * — which keys on the message's source URL — could not see it, and it broke
 * zero-console-error specs (measured as a `@smoke` flake, 2026-09-11 05:12Z).
 *
 * Publishing the origin makes that rule computable instead of guessed: the
 * harness already owns the denied-family list, so it joins on a fact this
 * module STATES rather than on a message substring anyone could break. No
 * token is re-spelled in the spec tree.
 *
 * A reason added to `web/manifests/metrics.yaml` widens `MapsFailureReason`
 * and this `switch` then fails to compile until somebody decides which of the
 * two it is. That decision is the point.
 */
function unreachableOriginFor(reason: MapsFailureReason): string | null {
  switch (reason) {
    case 'script_load_error':
      return MAPS_SDK_ORIGIN;
    case 'auth_failure':
      return null;
    case 'dev_watermark':
      return null;
    default:
      return assertNever(reason);
  }
}

const WATERMARK_TEXT = 'For development purposes only';
/** Stop DOM-watching this long after a map mounts — the watermark, if any, is up by then. */
const WATERMARK_OBSERVE_MS = 30_000;

// Emit each reason at most once per page load: gm_authFailure can fire repeatedly
// and the MutationObserver can see many mutations, but the signal is "this session
// saw a degraded map", not a per-event tally.
const reported = new Set<MapsFailureReason>();

function messageFor(reason: MapsFailureReason): string {
  switch (reason) {
    case 'auth_failure':
      return 'Google Maps auth failure (gm_authFailure): invalid key, referrer restriction, or billing disabled';
    case 'script_load_error':
      return 'Google Maps JS API script failed to load';
    case 'dev_watermark':
      return 'Google Maps rendered the "for development purposes only" watermark (quota/billing degraded)';
    default:
      return assertNever(reason);
  }
}

/**
 * Report a Maps failure to Loki (as a `component: 'GoogleMaps'` error log) and to
 * the `web_maps_degraded` counter. Deduped per reason per page load.
 */
export function reportMapsFailure(
  reason: MapsFailureReason,
  context: Record<string, unknown> = {}
): void {
  if (typeof window === 'undefined') return;
  if (reported.has(reason)) return;
  reported.add(reason);

  const unreachableOrigin = unreachableOriginFor(reason);
  clientLogger.error(
    {
      component: 'GoogleMaps',
      reason,
      ...(unreachableOrigin === null ? {} : { unreachableOrigin }),
      ...context,
    },
    messageFor(reason)
  );
  reportWebMapsDegraded(1, { reason });
}

/**
 * Install the `window.gm_authFailure` handler. Call this ONCE, as early as
 * possible (before any Maps script loads), so Google's callback has somewhere to
 * land — see entry-client.tsx.
 */
export function installMapsAuthFailureHandler(): void {
  if (typeof window === 'undefined') return;
  window.gm_authFailure = () => reportMapsFailure('auth_failure');
}

// One observer at a time — MapApiProvider can mount for several maps on a page.
let watching = false;

/**
 * Watch for the "for development purposes only" watermark and report it. Returns
 * a disconnect function. No-op (returns a noop) on the server, without
 * MutationObserver, or if already watching. Self-disconnects on first detection
 * or after {@link WATERMARK_OBSERVE_MS}.
 */
export function observeMapsWatermark(root: ParentNode = document.body): () => void {
  const noop = (): void => {};
  if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return noop;
  if (watching) return noop;

  // Already degraded before we started watching?
  if (root.textContent?.includes(WATERMARK_TEXT)) {
    reportMapsFailure('dev_watermark');
    return noop;
  }

  watching = true;
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.textContent?.includes(WATERMARK_TEXT)) {
          reportMapsFailure('dev_watermark');
          stop();
          return;
        }
      }
    }
  });
  observer.observe(root, { childList: true, subtree: true });
  const timer = window.setTimeout(stop, WATERMARK_OBSERVE_MS);

  function stop(): void {
    if (!watching) return;
    watching = false;
    observer.disconnect();
    window.clearTimeout(timer);
  }

  return stop;
}

/** Test-only: reset the per-page dedup + observer guard between cases. */
export function resetMapsHealthForTest(): void {
  reported.clear();
  watching = false;
}
